import { useState } from "react";
import type { Task, TechnicianAttachment } from "@myapp/contract";
import {
  useAddTaskTechnicianAttachments,
  useRemoveTaskTechnicianAttachment,
  useUpdateTaskTechnicianComment,
} from "../../../entities/task";
import { feedbackErrorMessage } from "./feedbackErrorMessage";
import type { TechnicianFeedbackController } from "./types";
import { useFeedbackActor } from "./useFeedbackActor";

/** Task twin of `useAppointmentTechnicianFeedback` — see there. */
export function useTaskTechnicianFeedback(task: Task | null): TechnicianFeedbackController | null {
  const { canRemove } = useFeedbackActor();
  const updateComment = useUpdateTaskTechnicianComment();
  const addAttachments = useAddTaskTechnicianAttachments();
  const removeAttachment = useRemoveTaskTechnicianAttachment();
  const [removingId, setRemovingId] = useState<number | null>(null);

  if (!task) return null;

  const { id } = task;

  return {
    comment: task.technicianComment ?? "",
    attachments: task.technicianAttachments ?? [],
    canRemove,

    saveComment: async (next) => {
      await updateComment
        .mutateAsync({ id, dto: { technicianComment: next.trim() || null } })
        .catch(() => undefined);
    },
    isSavingComment: updateComment.isPending,
    commentError: updateComment.error ? feedbackErrorMessage(updateComment.error) : null,

    addFiles: async (files) => {
      if (!files.length) return;
      await addAttachments.mutateAsync({ id, files }).catch(() => undefined);
    },
    isUploading: addAttachments.isPending,
    uploadError: addAttachments.error ? feedbackErrorMessage(addAttachments.error) : null,

    removeAttachment: async (item: TechnicianAttachment) => {
      setRemovingId(item.id);
      try {
        await removeAttachment.mutateAsync({ id, attachmentId: item.id });
      } catch {
        // surfaced through `removeError`
      } finally {
        setRemovingId(null);
      }
    },
    removingId,
    removeError: removeAttachment.error ? feedbackErrorMessage(removeAttachment.error) : null,
  };
}
